/**
 * What went wrong with a REST call, in the terms screens care about.
 * Every request through the clients fails with an ApiError, including network failures and
 * timeouts (`no-response`). Only cancellations and errors that did not come from axios pass
 * through unchanged. A request that deviates from the standard naming says so in its own
 * `@throws` comment.
 */
export type ApiErrorKind =
  /** 400 */
  | 'invalid-input'
  /** 401: wrong credentials, or an expired session on a call that opted out of the logout */
  | 'unauthorized'
  /** 403 */
  | 'forbidden'
  /** 404 */
  | 'not-found'
  /** 409 */
  | 'conflict'
  /** 429 */
  | 'rate-limited'
  /** Any 5xx */
  | 'server-error'
  /** 400 "Retailer name … already exists." from the partner create/update calls only */
  | 'duplicate-retailer-name'
  /** No response: network failure, timeout or offline */
  | 'no-response'
  /** Any other status, or not an HTTP error at all */
  | 'other';

interface ApiErrorDetails {
  status?: number;
  serverMessage?: string;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;

  /**
   * HTTP status; for showing the code on the error page, and for the one lookup that must tell
   * 503 from other server errors (`fetchUserPreferenceThrowingServiceUnavailable`).
   * Branch on `kind` otherwise.
   */
  readonly status?: number;

  /** The response body, when the server sent a non-empty string. */
  readonly serverMessage?: string;

  constructor(kind: ApiErrorKind, { status, serverMessage, cause }: ApiErrorDetails = {}) {
    super(`REST call failed: ${kind}`, { cause });
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

export const isApiError = (error: unknown, ...kinds: ApiErrorKind[]): error is ApiError =>
  error instanceof ApiError && (kinds.length === 0 || kinds.includes(error.kind));

/**
 * The server's own message when it sent one, "something went wrong" when it did not answer,
 * else the general "review" error.
 */
export const serverMessageOrGeneralError = (error: unknown): string =>
  (isApiError(error) && error.serverMessage)
  || (isApiError(error, 'no-response') ? 'common.errors.code-400' : 'common.errors.review-general');
